import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useQueries} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchCompanyAttendance} from '@/entities/attendance/api';
import {periodKey, type Period} from '@/entities/attendance/period';
import type {
  AttendanceSummary,
  ControlDepartment,
} from '@/entities/attendance/types';
import {formatCount} from '@/shared/lib/format';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon} from '@/shared/ui/icons';
import {Skeleton} from '@/shared/ui/skeleton';
import {Meter} from '@/shared/ui/stat-tile';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {attendanceRate, formatRate} from '../lib/aggregate';

/**
 * Every department for one period, as a ranking — worst first.
 *
 * The comparison used to be a seven-column table on a desktop and a grid of three
 * figures per card on a phone: two layouts, one of which was a horizontal scroll and
 * neither of which answered the question anybody asks of a department comparison, which
 * is «где хуже всего». A table is sorted by whatever column you last pressed and is read
 * cell by cell; a ranked bar chart is read in one pass, top to bottom, and its ORDER is
 * the answer.
 *
 * So it is one row per department at every width: the name, a bar of the attendance
 * rate, the rate itself, and the two counters worth carrying (absences, late arrivals).
 * A department with nothing wrong shows a full bar and no colour, which is the same
 * rule the rest of the report follows.
 *
 * ── The requests ────────────────────────────────────────────────────────────
 * There is no route that returns this in one call, so it is N parallel calls to
 * `GET /control/attendance?department_ids={one}` — one per department. Two notes:
 *
 *   - It is the COMPANY route with a filter, not `/control/departments/{id}/attendance`.
 *     Both answer the same summary, but the department route also returns every day
 *     row for every person in it, which for a comparison is several hundred kilobytes
 *     nobody looks at.
 *   - `useQueries`, so all N are in flight at once and each caches under its own key. A
 *     department that fails keeps its row and shows an em dash rather than vanishing —
 *     a department silently missing from a comparison is a department somebody concludes
 *     has nobody in it.
 *
 * The component only mounts when its section is opened (see `Disclosure`), which is
 * what makes N requests a deliberate act rather than the cost of arriving on a tab.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(1)},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingVertical: spacing(3),
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
  },
  body: {flex: 1, gap: spacing(2), minWidth: 0},
  head: {flexDirection: 'row', alignItems: 'baseline', gap: spacing(2)},
  name: {flex: 1, minWidth: 0},
  facts: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), flexWrap: 'wrap'},
  fact: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},
  dot: {width: 6, height: 6, borderRadius: 3},
});

type Row = {
  department: ControlDepartment;
  summary: AttendanceSummary | null;
  headcount: number | null;
  pending: boolean;
  rate: number | null;
};

export function DepartmentRanking({
  companyId,
  period,
  departments,
  onOpen,
}: {
  companyId: string;
  period: Period;
  departments: ControlDepartment[];
  onOpen: (department: ControlDepartment) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  const results = useQueries({
    queries: departments.map(dept => ({
      queryKey: [
        'attendance-department-summary',
        companyId,
        dept.department_id,
        periodKey(period),
      ],
      queryFn: () =>
        fetchCompanyAttendance(companyId, period, {
          departmentIds: [dept.department_id],
        }),
      staleTime: 60_000,
    })),
  });

  if (departments.length === 0) {
    return <EmptyState title={t('reports.noDepartments')} />;
  }

  const rows: Row[] = departments
    .map((department, i) => {
      const result = results[i];
      const summary = result?.data?.summary ?? null;
      return {
        department,
        summary,
        headcount: result?.data?.employees?.length ?? null,
        pending: result?.isPending ?? true,
        rate: summary ? attendanceRate(summary) : null,
      };
    })
    // Worst first, and anything with no answer yet (or no schedule to be judged
    // against) at the bottom: a row that cannot be ranked must not occupy the position
    // the eye reads as "this is the problem".
    .sort((a, b) => {
      if (a.rate == null && b.rate == null) {
        return a.department.name.localeCompare(b.department.name);
      }
      if (a.rate == null) {
        return 1;
      }
      if (b.rate == null) {
        return -1;
      }
      return a.rate - b.rate;
    });

  const fact = (color: string, value: number, label: string) => (
    <View style={styles.fact}>
      <View style={[styles.dot, {backgroundColor: color}]} />
      <AppText variant="mono">{formatCount(value)}</AppText>
      <AppText variant="micro">{label}</AppText>
    </View>
  );

  return (
    <View style={styles.root}>
      {rows.map(row => (
        <Pressable
          key={row.department.department_id}
          accessibilityRole="button"
          onPress={() => onOpen(row.department)}
          testID={`report-department-${row.department.department_id}`}
          style={({pressed}) => [styles.row, pressed && {backgroundColor: p.muted}]}>
          <View style={styles.body}>
            <View style={styles.head}>
              <AppText variant="subtitle" numberOfLines={1} style={styles.name}>
                {row.department.name}
              </AppText>
              <AppText variant="mono">
                {row.summary ? formatRate(row.rate) : '—'}
              </AppText>
            </View>

            {row.pending ? (
              <Skeleton height={6} radius={radii.pill} />
            ) : (
              <Meter
                value={row.rate == null ? 0 : Math.round(row.rate * 100)}
                max={100}
                tone={row.rate == null ? 'neutral' : row.rate === 1 ? 'good' : 'info'}
              />
            )}

            <View style={styles.facts}>
              <AppText variant="micro">
                {t('reports.headcount', {
                  count: row.headcount ?? row.department.personnel_count,
                })}
              </AppText>
              {row.summary && row.summary.absent_days > 0
                ? fact(p.destructive, row.summary.absent_days, t('reports.col.absent'))
                : null}
              {row.summary && row.summary.late_days > 0
                ? fact(p.warning, row.summary.late_days, t('reports.col.late'))
                : null}
            </View>
          </View>

          <Icon name="chevron" size={16} color={p.mutedForeground} />
        </Pressable>
      ))}
    </View>
  );
}
